import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store';

interface FavoritesModalProps {
  show: boolean;
  onHide: () => void;
}

interface FavoriteProject {
  id: string;
  name: string;
  url: string;
  description: string;
  stars: number;
  language: string;
  owner: string;
  addedAt: Date;
}

const FavoritesModal: React.FC<FavoritesModalProps> = ({ show, onHide }) => {
  const { favorites } = useAppStore();
  const [favoriteProjects, setFavoriteProjects] = useState<FavoriteProject[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'stars' | 'addedAt'>('addedAt');
  const [filterLanguage, setFilterLanguage] = useState('');

  useEffect(() => {
    // 模拟收藏项目数据
    const mockFavorites: FavoriteProject[] = [
      {
        id: '1',
        name: 'transformers',
        url: 'https://github.com/huggingface/transformers',
        description: '🤗 Transformers: State-of-the-art Machine Learning for Pytorch, TensorFlow, and JAX.',
        stars: 132000,
        language: 'Python',
        owner: 'huggingface',
        addedAt: new Date('2024-01-15')
      },
      {
        id: '2',
        name: 'pytorch',
        url: 'https://github.com/pytorch/pytorch',
        description: 'Tensors and Dynamic neural networks in Python with strong GPU acceleration',
        stars: 82000,
        language: 'Python',
        owner: 'pytorch',
        addedAt: new Date('2024-01-10')
      }
    ];
    setFavoriteProjects(mockFavorites);
  }, [favorites]);

  const filteredProjects = favoriteProjects
    .filter(project =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(project =>
      !filterLanguage || project.language === filterLanguage
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'stars':
          return b.stars - a.stars;
        case 'addedAt':
          return b.addedAt.getTime() - a.addedAt.getTime();
        default:
          return 0;
      }
    });

  const languages = [...new Set(favoriteProjects.map(p => p.language))];

  const removeFavorite = (projectId: string) => {
    setFavoriteProjects(prev => prev.filter(p => p.id !== projectId));
  };

  const exportFavorites = () => {
    const data = JSON.stringify(favoriteProjects, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-favorites.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content favorites-modal">
          <div className="modal-header favorites-header">
            <div className="favorites-title-section">
              <h3 className="modal-title">
                <i className="fas fa-heart text-danger me-2"></i>
                我的收藏夹
              </h3>
              <span className="favorites-count">
                共 {favoriteProjects.length} 个项目
              </span>
            </div>
            <div className="favorites-header-actions">
              <button
                className="btn btn-outline-primary btn-sm me-2"
                onClick={exportFavorites}
                title="导出收藏"
              >
                <i className="fas fa-download me-1"></i>
                导出
              </button>
              <button type="button" className="btn-close" onClick={onHide}></button>
            </div>
          </div>

          <div className="modal-body favorites-body">
            {/* 搜索和过滤器 */}
            <div className="favorites-filters">
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <div className="search-box">
                    <i className="fas fa-search search-icon"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="搜索收藏的项目..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterLanguage}
                    onChange={(e) => setFilterLanguage(e.target.value)}
                  >
                    <option value="">所有语言</option>
                    {languages.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                  >
                    <option value="addedAt">按收藏时间</option>
                    <option value="name">按名称</option>
                    <option value="stars">按Star数</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 收藏项目列表 */}
            <div className="favorites-list">
              {filteredProjects.length === 0 ? (
                <div className="empty-favorites">
                  <div className="empty-icon">
                    <i className="fas fa-heart-broken"></i>
                  </div>
                  <h4>暂无收藏项目</h4>
                  <p className="text-muted">
                    {searchTerm || filterLanguage ? '没有找到匹配的项目' : '开始收藏您感兴趣的项目吧！'}
                  </p>
                </div>
              ) : (
                <div className="row g-3">
                  {filteredProjects.map((project) => (
                    <div key={project.id} className="col-md-6 col-lg-4">
                      <div className="favorite-item">
                        <div className="favorite-header">
                          <div className="favorite-title">
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="project-link"
                            >
                              <i className="fab fa-github me-2"></i>
                              {project.name}
                            </a>
                          </div>
                          <div className="favorite-actions">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeFavorite(project.id)}
                              title="取消收藏"
                            >
                              <i className="fas fa-heart-broken"></i>
                            </button>
                          </div>
                        </div>

                        <div className="favorite-meta">
                          <div className="favorite-owner">
                            <i className="fas fa-user me-1"></i>
                            {project.owner}
                          </div>
                          <div className="favorite-stats">
                            <span className="stat-item">
                              <i className="fas fa-star text-warning me-1"></i>
                              {project.stars.toLocaleString()}
                            </span>
                            <span className="stat-item">
                              <i className="fas fa-code me-1"></i>
                              {project.language}
                            </span>
                          </div>
                        </div>

                        <div className="favorite-description">
                          {project.description}
                        </div>

                        <div className="favorite-footer">
                          <small className="text-muted">
                            <i className="fas fa-heart me-1"></i>
                            收藏于 {project.addedAt.toLocaleDateString('zh-CN')}
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer favorites-footer">
            <div className="favorites-footer-info">
              <small className="text-muted">
                显示 {filteredProjects.length} / {favoriteProjects.length} 个项目
              </small>
            </div>
            <div className="favorites-footer-actions">
              <button type="button" className="btn btn-secondary" onClick={onHide}>
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </div>
  );
};

export default FavoritesModal;
